<template>
    <b-card :body-class="bodyClass">
        <template v-slot:header>
            <timeline-header
                :date="surgery.date"
                :time="surgery.date"
                is-editable
                @edit="showModal"
            >
                <span>
                    <b>{{ surgery.description.surgery_type | choice(surgeryFields.description_options.children.surgery_type) }}</b> /
                    {{ surgery.description.description }}
                </span>
            </timeline-header>
        </template>

        <div class="d-flex flex-column">
            <display-field :field="surgeryFields.diagnosis" :value="surgery.diagnosis"/>
        </div>

        <div class="d-inline-flex flex-column mr-3">
            <display-choice-field :field="surgeryFields.anesthesia_type" :value="surgery.anesthesia_type"/>
            <display-choice-field :field="surgeryFields.clinic_response" :value="surgery.clinic_response"/>
        </div>

        <div class="d-inline-flex flex-column mb-3 mr-3">
            <display-field :field="surgeryFields.hospitalization_date">
                {{ surgery.hospitalization_date | datetime | valueOrEmpty }}
            </display-field>
            <display-field :field="surgeryFields.weight" :value="surgery.weight"/>
        </div>

        <timeline-adjuvant-treatment @save="saved" />

        <h5 class="text-primary my-3">{{ $t('surgery.detail.complications') }}</h5>

        <timeline-general-item
            v-for="(element, index) in surgery.complications"
            :key="'complication-' + index"
            class="my-3"
            :data="element"
            is-complication
            @edit="$emit('edit', $event)"
            @delete="$emit('delete', $event)"
            @save="saved"
        />

        <timeline-create-general-item
            :date="surgery.date"
            :surgery-id="surgery.id"
            :index="'complication-' + index"
            class="my-3"
            is-complication
            @save="saved"
        />

        <h5 class="text-primary my-3">{{ $t('surgery.detail.events') }}</h5>

        <timeline-general-item
            v-for="(element, index) in surgeryElements"
            :key="'events-' + index"
            class="my-3"
            :data="element"
            @edit="$emit('edit', $event)"
            @delete="$emit('delete', $event)"
            @save="saved"
        />

        <timeline-create-general-item
            :date="surgery.date"
            :surgery-id="surgery.id"
            :index="'events-' + index"
            class="my-3"
            @save="saved"
        />

        <b-modal
            :id="modalId"
            size="xl"
            centered
            hide-header
            hide-footer
        >
            <surgery-data
                class="add-surgery-page"
                ref="surgeryData"
            />
            <p v-if="generalError" class="invalid-feedback invalid">
                {{ this.$t('error.general') }}
            </p>

            <create-cancel-buttons
                :ok-title="$t('utils.save')"
                :ok-function="save"
                :cancel-title="$t('utils.cancel')"
                :cancel-function="cancel"
            />
        </b-modal>

    </b-card>
</template>

<script>
    import {mapState} from "vuex";
    import TimelineGeneralItem from "@/components/surgery/detail/timeline/TimelineGeneralItem";
    import {extractDate} from "@/utils/formatting";
    import TimelineHeader from "@/components/surgery/detail/timeline/TimelineHeader";
    import TimelineElementMixin from "@/components/surgery/detail/timeline/TimelineElementMixin";
    import TimelineCreateGeneralItem from "@/components/surgery/detail/timeline/TimelineCreateGeneralItem";
    import SurgeryData from "@/components/surgery/create/SurgeryData";
    import EditableByPopupMixin from "@/components/surgery/detail/timeline/EditableByPopupMixin";
    import TimelineAdjuvantTreatment from "@/components/surgery/detail/timeline/TimelineAdjuvantTreatment";
    import DisplayField from "@/components/DisplayField";
    import CreateCancelButtons from "@/components/surgery/detail/elements/CreateCancelButtons";
    import DisplayChoiceField from "@/components/DisplayChoiceField";
    import {logRequestError} from "@/utils/utils";

    export default {
        name: "TimelineSurgery",
        components: {
            DisplayChoiceField,
            CreateCancelButtons,
            DisplayField,
            TimelineAdjuvantTreatment,
            SurgeryData,
            TimelineCreateGeneralItem,
            TimelineHeader,
            TimelineGeneralItem,
        },
        mixins: [TimelineElementMixin, EditableByPopupMixin],
        computed: {
            ...mapState([
                'surgery',
                'surgeryFields',
                'bloodTestFields',
                'transfusionFields',
                'novoeightTreatmentFields',
                'adjuvantTreatmentFields',
            ]),
            surgeryElements() {
                let elements = this.surgery.post_surgery_days
                    .filter(x => x.date === extractDate(this.surgery.date));
                if (elements.length === 1) {
                    return elements[0].data;
                }
                return [];
            },
        },
        methods: {
            async save() {
                if (await this.$refs.surgeryData.validate()) {
                    try {
                        await this.updateSurgery({pk: this.surgery.id, data: this.surgery});
                    } catch (error) {
                        logRequestError(error);
                        this.generalError = true;
                    }
                    this.closeModal();
                    this.saved();
                    this.generalError = false;
                }
            },
            saved() {
                this.$emit('save');
            }
        },
    };
</script>

